/* import __COLOCATED_TEMPLATE__ from './edit-shipping-address-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import ajax from 'embercom/lib/ajax';
import { action } from '@ember/object';
import type Session from 'embercom/services/session';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'ember-intl/services/intl';
import PhoneCountryCodes, { type CountryCode } from 'embercom/lib/phone-codes';
import phoneCodes from 'embercom/lib/phone-codes';
import type { ShippingAddress, ShopifyOrder } from '../shopify';

interface Signature {
  Args: {
    onModalClose: () => void;
    isOpen: boolean;
    shopifyDomain: string | null;
    currentShippingAddress: ShippingAddress | null;
    onShippingAddressUpdate?: (orderId: number, newShippingAddress: ShippingAddress) => void;
    shopifyOrderId: number | null;
  };
}

export default class EditShippingAddressModal extends Component<Signature> {
  @service session!: Session;
  @service intl!: IntlService;
  @service declare notificationsService: any;

  @tracked selectedCountryCode: CountryCode = 'US';
  @tracked selectedPhoneCode: string = phoneCodes[this.selectedCountryCode].dial_code;
  @tracked phone: string | null = null;
  @tracked shippingAddress: ShippingAddress = {
    first_name: null,
    last_name: null,
    address1: null,
    address2: null,
    city: null,
    province: null,
    country: null,
    zip: null,
    phone: null,
  };

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this.shippingAddress.country = this.intl.t(this.selectedCountry.nameTranslationKey);
  }

  get countries() {
    return Object.entries(PhoneCountryCodes).map(([code, country]) => ({
      text: this.intl.t(country.nameTranslationKey),
      value: code,
    }));
  }

  get countryPhoneCodes() {
    return Object.entries(PhoneCountryCodes).map(([code, country]) => ({
      text: `${country.dial_code}`,
      value: code,
    }));
  }

  get selectedCountry() {
    return phoneCodes[this.selectedCountryCode];
  }

  @action
  setPhone() {
    this.shippingAddress.phone = this.phone ? this.selectedPhoneCode + this.phone : null;
  }

  @action
  selectCountry(countryCode: CountryCode) {
    this.selectedCountryCode = countryCode;
    this.selectedPhoneCode = phoneCodes[countryCode].dial_code;

    this.shippingAddress.country = this.intl.t(this.selectedCountry.nameTranslationKey);
  }

  @dropTask
  *saveShippingAddressTask(): Generator<any, void, any> {
    try {
      let data = {
        app_id: this.session.workspace.id,
        shopify_domain: this.args.shopifyDomain,
        new_shipping_address: this.shippingAddress,
      };

      let response = yield ajax({
        type: 'PUT',
        url: `/ember/inbox/shopify/update_order_shipping_address/${this.args.shopifyOrderId}`,
        data: JSON.stringify(data),
      }) as ShopifyOrder;

      this.args.onModalClose();
      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'inbox.conversation-details-sidebar.shopify.address.edit-shipping-address-modal.success-message',
        ),
      );

      if (this.args.onShippingAddressUpdate && this.args.shopifyOrderId !== null) {
        this.args.onShippingAddressUpdate(this.args.shopifyOrderId, response.shipping_address);
      }
    } catch (error) {
      captureException(error);
      let response = error.jqXHR.responseJSON.error;
      if (response?.key === 'embercom_shopify_update_incorrect') {
        this.notificationsService.notifyResponseError(error, {
          default:
            response?.message ||
            this.intl.t(
              'inbox.conversation-details-sidebar.shopify.address.edit-shipping-address-modal.error.incorrect-address',
            ),
        });
      } else {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t(
            'inbox.conversation-details-sidebar.shopify.address.edit-shipping-address-modal.error.general',
          ),
        });
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::EditShippingAddressModal': typeof EditShippingAddressModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/edit-shipping-address-modal': typeof EditShippingAddressModal;
  }
}
